import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU32, readU32, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U32 } from "./limits.ts";
import { MAX_PARTY_PLACE } from "./party-positions.ts";

/**
 * The `partyVitals` frame (ADR-101 decision 20, PROTOCOL.md 5.21): server to client, the vitals of the receiver's
 * party members its map server holds on its map. After the header: a `u32` tick, a `u8` count of 0 to 5, then 3 bytes
 * a member, a `u8` place in the party roster (0 to 5), a `u8` health in 255ths of its maximum (0 only when dead, at
 * least 1 while alive) and a `u8` state (bits 0 and 1 the flag state as the snapshot's flag component writes it, bit 2
 * dead, bits 3 to 7 reserved and written 0; a page ignores bits it does not know). A map server writes it into a
 * buffer it makes once a session (`putPartyVitalsEntry`, `putPartyVitalsHeader`), so a send allocates nothing; the
 * decoder fills a record made once, allocates nothing, never throws and never reads past the frame (ADR-030).
 */

/**
 * Bytes before the first member: the frame header, the tick and the count.
 */
export const PARTY_VITALS_HEADER_BYTES = HEADER_BYTES + 5;

/**
 * Bytes of one member: its place, its health and its state.
 */
export const PARTY_VITALS_ENTRY_BYTES = 3;

/**
 * The most members one frame carries: a party of 6 less the receiver.
 */
export const MAX_PARTY_VITALS = 5;

/**
 * The state byte's bits: the flag state in bits 0 and 1, dead in bit 2. The rest are reserved and written 0.
 */
export const PartyVitalsState = { FlagMask: 3, Dead: 4 } as const;

const COUNT_OFFSET = HEADER_BYTES + 4;
const RESERVED_STATE_BITS = 0xf8;
const MAX_BYTE = 255;

/**
 * One member to encode: its place in the party roster, its health in 255ths of its maximum and its state byte.
 */
export interface PartyVital {
  readonly place: number;
  readonly health: number;
  readonly state: number;
}

/**
 * A decoded `partyVitals`, reused across frames: the tick, how many members the frame held, and their places,
 * healths and states, the first `count` of each valid.
 */
export interface PartyVitalsRecord {
  tick: number;
  count: number;
  readonly places: Uint8Array;
  readonly healths: Uint8Array;
  readonly states: Uint8Array;
}

/**
 * A fresh record for `decodePartyVitals` to fill, with room for the most members a frame carries.
 *
 * @returns An empty record.
 */
export function newPartyVitalsRecord(): PartyVitalsRecord {
  return { tick: 0, count: 0, places: new Uint8Array(MAX_PARTY_VITALS), healths: new Uint8Array(MAX_PARTY_VITALS), states: new Uint8Array(MAX_PARTY_VITALS) };
}

/**
 * Bytes of a frame of a number of members.
 *
 * @param count The members.
 * @returns `8 + 3 * count`.
 */
export function partyVitalsBytes(count: number): number {
  return PARTY_VITALS_HEADER_BYTES + PARTY_VITALS_ENTRY_BYTES * count;
}

/**
 * Writes one member at its place in a frame through plain byte stores. The caller has sized the buffer and checked the
 * fields' widths, as a map server does with values it holds in typed arrays of that width.
 *
 * @param buffer The frame.
 * @param index The member's place in the frame, from 0.
 * @param place Its place in the party roster.
 * @param health Its health in 255ths of its maximum.
 * @param state Its state byte.
 */
export function putPartyVitalsEntry(buffer: Uint8Array, index: number, place: number, health: number, state: number): void {
  const at = PARTY_VITALS_HEADER_BYTES + PARTY_VITALS_ENTRY_BYTES * index;
  buffer[at] = place;
  buffer[at + 1] = health;
  buffer[at + 2] = state;
}

/**
 * Writes a frame's header once its members are in place.
 *
 * @param buffer The frame.
 * @param tick The server tick, a `u32`.
 * @param count The members written, 0 to 5.
 * @returns The frame's length in bytes.
 */
export function putPartyVitalsHeader(buffer: Uint8Array, tick: number, count: number): number {
  putHeader(buffer, MessageType.PartyVitals);
  putU32(buffer, HEADER_BYTES, tick);
  buffer[COUNT_OFFSET] = count;
  return partyVitalsBytes(count);
}

/**
 * Encodes a `partyVitals` into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `partyVitalsBytes(members.length)` long.
 * @param tick The server tick.
 * @param members 0 to 5 members.
 * @returns Bytes written.
 * @throws Error when there are more than 5 members, the buffer is too small, a place is past 5, a field does not fit
 *   its width, a state sets a reserved bit or a member at health 0 is not dead.
 */
export function encodePartyVitals(buffer: Uint8Array, tick: number, members: readonly PartyVital[]): number {
  if (members.length > MAX_PARTY_VITALS) {
    throw new Error(`partyVitals carries 0 to ${MAX_PARTY_VITALS} members, not ${members.length}`);
  }
  const length = partyVitalsBytes(members.length);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte partyVitals`);
  }
  assertField(tick, MAX_U32, `partyVitals tick ${tick}`);
  for (let index = 0; index < members.length; index += 1) {
    const member = members[index] as PartyVital;
    assertField(member.place, MAX_PARTY_PLACE, `partyVitals place ${member.place}`);
    assertField(member.health, MAX_BYTE, `partyVitals health ${member.health}`);
    assertField(member.state, MAX_BYTE, `partyVitals state ${member.state}`);
    if ((member.state & RESERVED_STATE_BITS) !== 0) {
      throw new Error(`partyVitals state ${member.state} sets a reserved bit`);
    }
    if (member.health === 0 && (member.state & PartyVitalsState.Dead) === 0) {
      throw new Error(`partyVitals place ${member.place} is at health 0 and not dead`);
    }
    putPartyVitalsEntry(buffer, index, member.place, member.health, member.state);
  }
  return putPartyVitalsHeader(buffer, tick, members.length);
}

/**
 * Decodes a `partyVitals` frame into a record. Never throws and reads no byte past the frame: a frame shorter than its
 * header, a count past 5, a length other than `8 + 3 * count`, a place past 5 or another type is malformed, and another
 * version a mismatch, each leaving the record untouched. A count of 0 is well formed: no member is on the map. State
 * bits beyond the ones named are read as sent.
 *
 * @param frame The whole frame.
 * @param out Record to fill.
 * @returns The verdict.
 */
export function decodePartyVitals(frame: Uint8Array, out: PartyVitalsRecord): DecodeVerdict {
  if (frame.length < PARTY_VITALS_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = frame[COUNT_OFFSET] as number;
  if (count > MAX_PARTY_VITALS) {
    return MALFORMED;
  }
  const header = checkHeader(frame, MessageType.PartyVitals, partyVitalsBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  for (let index = 0; index < count; index += 1) {
    if ((frame[PARTY_VITALS_HEADER_BYTES + PARTY_VITALS_ENTRY_BYTES * index] as number) > MAX_PARTY_PLACE) {
      return MALFORMED;
    }
  }
  for (let index = 0; index < count; index += 1) {
    const at = PARTY_VITALS_HEADER_BYTES + PARTY_VITALS_ENTRY_BYTES * index;
    out.places[index] = frame[at] as number;
    out.healths[index] = frame[at + 1] as number;
    out.states[index] = frame[at + 2] as number;
  }
  out.tick = readU32(frame, HEADER_BYTES);
  out.count = count;
  return OK;
}
